/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { useCallback } from 'preact/hooks';
import { cssProps } from '@oracle/oraclejet-cssprops';
import { FormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { InlineUserAssistance } from '../UNSAFE_UserAssistance';

type InlineUserAssistanceProps = Pick<
  ComponentProps<typeof InlineUserAssistance>,
  | 'assistiveText'
  | 'fieldLabel'
  | 'helpSourceLink'
  | 'helpSourceText'
  | 'id'
  | 'userAssistanceDensity'
>;

const rootStyles = cssProps`${{
  borderTopColor: 'rgba(var(--oj-c-PRIVATE-DO-NOT-USE-palette-neutral-rgb-190), 0.1)',
  borderTopStyle: 'solid',
  borderTopWidth: '1px',
  paddingBottom: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)',
  paddingLeft: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)',
  paddingRight: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)',
  paddingTop: 'var(--oj-c-PRIVATE-DO-NOT-USE-core-spacing-2x)'
}}`;

export function DropdownUserAssistance(props: InlineUserAssistanceProps) {
  // Prevent the focus from transferring when the user clicks on an empty/non-clickable area
  // of the user assistance
  const handleMouseDown = useCallback((event: MouseEvent) => {
    // Don't call preventDefault when the user clicks on the help source link, so that the
    // focus is allowed to transfer in that case.
    if ((event.target as Element)?.tagName !== 'A') {
      event.preventDefault();
    }
  }, []);

  return (
    <FormFieldContext.Provider value={{ isFocused: true }}>
      <div class={rootStyles} onMouseDown={handleMouseDown}>
        <InlineUserAssistance {...props} />
      </div>
    </FormFieldContext.Provider>
  );
}
